import React, { useState, useRef, useMemo } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  InputBase,
  IconButton,
  Paper,
  Popper,
  ClickAwayListener,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Button,
  Menu,
  MenuItem,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import * as XLSX from "xlsx";
import { axiosInstance } from "src/network/adapter";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { ApiEndPoints } from "src/network/endpoints";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import UpgradePlanDialog from "src/@core/components/UpgradePlanDialog";
import { setShowSubscriptionPlanDialog } from "src/redux/slices/AuthSlice";
import useBusinessPermissions from "src/hooks/useBusinessPermissions";
import { useTranslation } from "react-i18next";

export default function MenuOption({
  createOnMenuClick,
  onEditMenu,
  isCatalog,
  onTabChange,
  onAddCategory,
  onEditCategory,
  onEditItem,
  onAddItem,
}) {
  const { t } = useTranslation("dashboard");
  const { isViewOnly, hasPermission } = useBusinessPermissions();
  const canManageMenu = hasPermission('manageMenu');
  const isDisabled = isViewOnly() && !canManageMenu;
  
  const [tab, setTab] = useState(0);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [menuToDelete, setMenuToDelete] = useState(null);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [upgradeDialogConfig, setUpgradeDialogConfig] = useState({
    featureType: "menus",
    currentQuantity: 0,
    maxQuantity: 0,
  });
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [showImportResultDialog, setShowImportResultDialog] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Category import/export state
  const [categoryExportMenuAnchor, setCategoryExportMenuAnchor] = useState(null);
  const [categoryImportLoading, setCategoryImportLoading] = useState(false);
  const [showCategoryImportResultDialog, setShowCategoryImportResultDialog] = useState(false);
  const [categoryImportResult, setCategoryImportResult] = useState(null);
  
  // Menu import/export state
  const [menuImportLoading, setMenuImportLoading] = useState(false);
  const [showMenuImportResultDialog, setShowMenuImportResultDialog] = useState(false);
  const [menuImportResult, setMenuImportResult] = useState(null);

  const dispatch = useDispatch();

  const infoIconRef = useRef(null);
  const fileInputRef = useRef(null);
  const categoryFileInputRef = useRef(null);
  const menuFileInputRef = useRef(null);
  const queryClient = useQueryClient();
  const selectedBusinessId = useSelector(
    (state) => state.auth.selectedBusinessId
  );

  const user = useSelector((state) => state.auth.user);

  const selectedBusiness =
    user?.business?.filter(
      (business) => business._id === selectedBusinessId
    )[0] || null;

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTab(newValue);
    setSearch(""); // Clear search when switching tabs
    if (onTabChange) {
      onTabChange(newValue);
    }
  };

  // Delete menu mutation
  const deleteMenuMutation = useMutation(
    async (menuId) => {
      const response = await axiosInstance.delete(
        `${ApiEndPoints.MENU.add}/${menuId}`
      );
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(t("menuOptionMenuDeleted"));
        queryClient.invalidateQueries(["menus", selectedBusinessId, isCatalog]);
        setShowDeleteDialog(false);
        setMenuToDelete(null);
      },
      onError: (error) => {
        console.error("Error deleting menu:", error);
        toast.error(t("menuOptionFailedDeleteMenu"));
      },
    }
  );

  // Delete category mutation
  const deleteCategoryMutation = useMutation(
    async (categoryId) => {
      const response = await axiosInstance.delete(
        `${ApiEndPoints.MENU_CATEGORY.delete}/${categoryId}`
      );
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(t("menuOptionCategoryDeleted"));
        queryClient.invalidateQueries([
          "menuCategory",
          selectedBusinessId,
          isCatalog,
        ]);
        setShowDeleteDialog(false);
        setMenuToDelete(null);
      },
      onError: (error) => {
        console.error("Error deleting category:", error);
        toast.error(t("menuOptionFailedDeleteCategory"));
      },
    }
  );

  // Delete menu item mutation
  const deleteMenuItemMutation = useMutation(
    async (itemId) => {
      const response = await axiosInstance.delete(
        `${ApiEndPoints.MENU_ITEM.add}/${itemId}`
      );
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(t("menuOptionItemDeleted"));
        queryClient.invalidateQueries([
          "menuItems",
          selectedBusinessId,
          isCatalog,
        ]);
        setShowDeleteDialog(false);
        setMenuToDelete(null);
      },
      onError: (error) => {
        console.error("Error deleting item:", error);
        toast.error(t("menuOptionFailedDeleteItem"));
      },
    }
  );

  // Bulk import menu items mutation
  const bulkImportMenuItemsMutation = useMutation(
    async (items) => {
      const response = await axiosInstance.post(
        ApiEndPoints.MENU_ITEM.bulkCreate(selectedBusinessId),
        { items }
      );
      return response.data;
    },
    {
      onSuccess: (data) => {
        setImportLoading(false);
        
        // Check if there are any failed items or if all succeeded
        const resultData = data?.data || data;
        const successfulCount = resultData?.successful?.count || 0;
        const failedCount = resultData?.failed?.count || 0;
        const totalProcessed = resultData?.totalProcessed || resultData?.summary?.total || 0;

        // Always show the result dialog if there are results
        if (totalProcessed > 0) {
          setImportResult(resultData);
          setShowImportResultDialog(true);
        }

        // Show appropriate toast message
        if (failedCount === 0 && successfulCount > 0) {
          toast.success(t("menuOptionAllItemsImported", { count: successfulCount }));
        } else if (successfulCount > 0 && failedCount > 0) {
          toast.success(t("menuOptionItemsImportedFailed", { success: successfulCount, failed: failedCount }));
        } else if (failedCount > 0) {
          toast.error(t("menuOptionItemsFailedImport", { count: failedCount }));
        }

        // Refresh menu items list
        queryClient.invalidateQueries([
          "menuItems",
          selectedBusinessId,
          isCatalog,
        ]);
        
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      },
      onError: (error) => {
        console.error("Error importing items:", error);
        const errorMessage =
          error?.response?.data?.message ||
          t("menuOptionFailedImportItems");
        toast.error(errorMessage);
        setImportLoading(false);
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      },
    }
  );

  // Bulk import menus mutation
  const bulkImportMenusMutation = useMutation(
    async (items) => {
      const response = await axiosInstance.post(
        ApiEndPoints.MENU.bulkCreate(selectedBusinessId),
        { items }
      );
      return response.data;
    },
    {
      onSuccess: (data) => {
        setMenuImportLoading(false);
        
        // Check if there are any failed items or if all succeeded
        const resultData = data?.data || data;
        const successfulCount = resultData?.successful?.count || 0;
        const failedCount = resultData?.failed?.count || 0;
        const totalProcessed = resultData?.totalProcessed || resultData?.summary?.total || 0;

        // Always show the result dialog if there are results
        if (totalProcessed > 0) {
          setMenuImportResult(resultData);
          setShowMenuImportResultDialog(true);
        }

        // Show appropriate toast message
        if (failedCount === 0 && successfulCount > 0) {
          toast.success(t("menuOptionAllMenusImported", { count: successfulCount }));
        } else if (successfulCount > 0 && failedCount > 0) {
          toast.success(t("menuOptionMenusImportedFailed", { success: successfulCount, failed: failedCount }));
        } else if (failedCount > 0) {
          toast.error(t("menuOptionMenusFailedImport", { count: failedCount }));
        }

        // Refresh menus list
        queryClient.invalidateQueries([
          "menus",
          selectedBusinessId,
          isCatalog,
        ]);
        
        // Reset file input
        if (menuFileInputRef.current) {
          menuFileInputRef.current.value = "";
        }
      },
      onError: (error) => {
        console.error("Error importing menus:", error);
        toast.error(error.response?.data?.message || t("menuOptionFailedImportMenus"));
        setMenuImportLoading(false);
        if (menuFileInputRef.current) {
          menuFileInputRef.current.value = "";
        }
      },
    }
  );

  // Bulk import categories mutation
  const bulkImportCategoriesMutation = useMutation(
    async (items) => {
      const response = await axiosInstance.post(
        ApiEndPoints.MENU_CATEGORY.bulkCreate(selectedBusinessId),
        { items }
      );
      return response.data;
    },
    {
      onSuccess: (data) => {
        setCategoryImportLoading(false);
        
        // Check if there are any failed items or if all succeeded
        const resultData = data?.data || data;
        const successfulCount = resultData?.successful?.count || 0;
        const failedCount = resultData?.failed?.count || 0;
        const totalProcessed = resultData?.totalProcessed || resultData?.summary?.total || 0;

        // Always show the result dialog if there are results
        if (totalProcessed > 0) {
          setCategoryImportResult(resultData);
          setShowCategoryImportResultDialog(true);
        }

        // Show appropriate toast message
        if (failedCount === 0 && successfulCount > 0) {
          toast.success(t("menuOptionAllCategoriesImported", { count: successfulCount }));
        } else if (successfulCount > 0 && failedCount > 0) {
          toast.success(t("menuOptionCategoriesImportedFailed", { success: successfulCount, failed: failedCount }));
        } else if (failedCount > 0) {
          toast.error(t("menuOptionCategoriesFailedImport", { count: failedCount }));
        }

        // Refresh categories list
        queryClient.invalidateQueries([
          "menuCategory",
          selectedBusinessId,
          isCatalog,
        ]);
        
        // Reset file input
        if (categoryFileInputRef.current) {
          categoryFileInputRef.current.value = "";
        }
      },
      onError: (error) => {
        console.error("Error importing categories:", error);
        const errorMessage =
          error?.response?.data?.message ||
          t("menuOptionFailedImportCategories");
        toast.error(errorMessage);
        setCategoryImportLoading(false);
        // Reset file input
        if (categoryFileInputRef.current) {
          categoryFileInputRef.current.value = "";
        }
      },
    }
  );

  const { data: menus } = useQuery(
    ["menus", selectedBusinessId, isCatalog],
    async ({ signal }) => {
      const res = await axiosInstance.get(
        `${ApiEndPoints.MENU.get}/${selectedBusinessId}`,
        {
          signal,
          params: {
            isCatalog: isCatalog,
          },
        }
      );
      return res.data.data;
    },
    {
      refetchOnWindowFocus: false,
      cacheTime: 30 * 60 * 1000,
      enabled:
        !!selectedBusinessId && isCatalog !== undefined && isCatalog !== null,
    }
  );

  const { data: menuCategory } = useQuery(
    ["menuCategory", selectedBusinessId, isCatalog],
    async ({ signal }) => {
      const res = await axiosInstance.get(
        `${ApiEndPoints.MENU_CATEGORY.get}/${selectedBusinessId}`,
        {
          signal,
          params: {
            isCatalog: isCatalog,
          },
        }
      );
      return res.data.data;
    },
    {
      refetchOnWindowFocus: false,
      cacheTime: 30 * 60 * 1000,
      enabled: !!selectedBusinessId && tab === 1, // Only fetch when categories tab is active
    }
  );

  // Add query for menu items using the proper GET endpoint
  const { data: menuItems } = useQuery(
    ["menuItems", selectedBusinessId, isCatalog],
    async ({ signal }) => {
      const res = await axiosInstance.get(
        `${ApiEndPoints.MENU_ITEM.get}/${selectedBusinessId}`,
        {
          signal,
          params: {
            isCatalog: isCatalog,
          },
        }
      );
      return res.data.data;
    },
    {
      refetchOnWindowFocus: false,
      cacheTime: 30 * 60 * 1000,
      enabled: !!selectedBusinessId && tab === 2, // Only fetch when items tab is active
    }
  );

  // Add query for business locations to help with import mapping
  const { data: businessLocations } = useQuery(
    ["businessLocations", selectedBusinessId],
    async ({ signal }) => {
      const res = await axiosInstance.get(
        `${ApiEndPoints.BUSINESS_LOCATION.getAll}/${selectedBusinessId}`,
        { signal }
      );
      return res.data.data;
    },
    {
      enabled: !!selectedBusinessId,
      refetchOnWindowFocus: false,
    }
  );

  // Create a map for name to ID for easy lookup during import
  const locationMap = useMemo(() => {
    const map = {};
    if (businessLocations && Array.isArray(businessLocations)) {
      businessLocations.forEach((loc) => {
        map[loc.businessLocationName.toLowerCase().trim()] = loc._id;
      });
    }
    return map;
  }, [businessLocations]);

  // Filter menus by search
  const filteredMenus =
    menus?.menus && Array.isArray(menus.menus)
      ? menus?.menus.filter((menu) =>
          menu.name.toLowerCase().includes(search.trim().toLowerCase())
        )
      : [];

  // Filter categories by search
  const filteredCategories =
    menuCategory?.categories && Array.isArray(menuCategory.categories)
      ? menuCategory?.categories.filter((category) =>
          category.name.toLowerCase().includes(search.trim().toLowerCase())
        )
      : [];

  // Filter items by search
  const filteredItems = useMemo(() => {
    if (!menuItems) return [];

    // Handle the API response structure: data.items
    let items = [];
    if (menuItems.items && Array.isArray(menuItems.items)) {
      items = menuItems.items;
    } else if (Array.isArray(menuItems)) {
      items = menuItems;
    } else if (menuItems.data && Array.isArray(menuItems.data)) {
      items = menuItems.data;
    }

    return items.filter(
      (item) =>
        item.name &&
        item.name.toLowerCase().includes(search.trim().toLowerCase())
    );
  }, [menuItems, search]);

  // Get the appropriate filtered data based on active tab
  const getFilteredData = () => {
    switch (tab) {
      case 0: // Menu tab
        return filteredMenus;
      case 1: // Categories tab
        return filteredCategories;
      case 2: // Items tab
        return filteredItems;
      default:
        return [];
    }
  };

  // Get tab labels based on isCatalog (i18n)
  const getTabLabels = () => {
    if (isCatalog) {
      return [t("menuOptionTabCatalog"), t("menuOptionTabCategories"), t("menuOptionTabItems")];
    }
    return [t("menuOptionTabMenu"), t("menuOptionTabCategories"), t("menuOptionTabItems")];
  };

  const handleEditMenu = (menu) => {
    if (onEditMenu) {
      onEditMenu(menu);
    }
  };

  const handleDeleteMenu = (menu) => {
    setMenuToDelete(menu);
    setShowDeleteDialog(true);
  };

  const handleEditCategory = (category) => {
    if (onEditCategory) {
      onEditCategory(category);
    }
  };

  const handleDeleteCategory = (category) => {
    setMenuToDelete(category);
    setShowDeleteDialog(true);
  };

  const handleEditItem = (item) => {
    // Navigate to item creation/editing
    if (onEditItem) {
      onEditItem(item);
    }
  };

  const handleDeleteItem = (item) => {
    setMenuToDelete(item);
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    if (menuToDelete) {
      if (tab === 0) {
        // Delete menu
        deleteMenuMutation.mutate(menuToDelete._id);
      } else if (tab === 1) {
        // Delete category
        deleteCategoryMutation.mutate(menuToDelete._id);
      } else if (tab === 2) {
        // Delete item
        deleteMenuItemMutation.mutate(menuToDelete._id);
      }
    }
  };

  const handleAddCategory = () => {
    if (onAddCategory) {
      onAddCategory();
    }
  };

  const handleAddItem = () => {
    if (onAddItem) {
      onAddItem();
    }
  };

  // Check if items are available for export
  const hasItemsForExport = () => {
    if (!menuItems) return false;
    if (menuItems.items && Array.isArray(menuItems.items) && menuItems.items.length > 0) return true;
    if (Array.isArray(menuItems) && menuItems.length > 0) return true;
    if (menuItems.data && Array.isArray(menuItems.data) && menuItems.data.length > 0) return true;
    return false;
  };

  // Transform menu items data for export
  const transformItemsForExport = (items) => {
    if (!items || !Array.isArray(items)) return [];
    
    const exportRows = [];
    
    items.forEach((item) => {
      // Common fields for all rows
      const ingredients = item.ingredients?.map((ing) => ing.name).join(", ") || "";
      const allergens = Array.isArray(item.allergens) ? item.allergens.join(", ") : "";
      const assetUrls = Array.isArray(item.imageVideo) ? item.imageVideo.join(", ") || "" : "";
      
      // Helper to format nutritional info
      const formatNutritionalInfo = (nutrition) => {
        if (!nutrition) return "";
        return `Weight:${nutrition.weight || 0}, Kcal:${nutrition.kcal || 0}, Protein:${nutrition.protein || 0}, Carbs:${nutrition.carbohydrates || 0}, Fats:${nutrition.fats || 0}, Fibre:${nutrition.fibre || 0}`;
      };
      
      // Helper to format date from YYYY-MM-DD to MM/DD/YYYY
      const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return dateStr;
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
      };
      
      if (item.variantEnabled && item.variants && item.variants.length > 0) {
        // Export each variant as a separate row
        item.variants.forEach((variant) => {
          // Get location stock for first location (Location 1)
          const locationStock = variant.perLocationStock?.[0] || {};
          const locationName = locationStock.locationId?.businessLocationName || locationStock.locationId || "";
          
          const row = {
            "Item Code": variant.code || item.code || "",
            "Item Name": item.name || "",
            "Description": variant.description || item.description || "",
            "Ingredients": ingredients,
            "Allergens/Preparation method": allergens,
            "Nutritional info": formatNutritionalInfo(variant.nutrition),
            "Prep time": item.prepTime || "",
            "Wait time": item.waitTime || "",
            "Currency": item.currency || "",
            "Price": item.price || "",
            "SKU": variant.sku || item.sku || "",
            "GTIN": variant.gtin || item.gtin || "",
            "Variant": variant.name || "Full",
            "Variant desc": variant.description || "",
            "Variant currency": locationStock.currency || variant.currency || "",
            "Variant price": locationStock.price || variant.price || "",
            "Asset URLs": assetUrls,
            "Location 1": locationName,
            "Stock on hand 1": locationStock.stockOnHand || 0,
            "Stock committed 1": locationStock.stockCommitted || 0,
            "Awaiting new stock delivery 1": formatDate(locationStock.awaitingNewStockDelivery),
            "Awaiting new stock count 1": locationStock.awaitingNewStockCount || 0,
            "Show stock count to customer 1": variant.showStockToCustomer !== undefined 
              ? (variant.showStockToCustomer ? "Yes" : "No")
              : (item.showStockToCustomer ? "Yes" : "No"),
            "Allow sale if out of stock 1": variant.allowSaleIfOutOfStock !== undefined
              ? (variant.allowSaleIfOutOfStock ? "Yes" : "No")
              : (item.allowSaleIfOutOfStock ? "Yes" : "No"),
          };
          
          exportRows.push(row);
        });
      } else {
        // No variant - export as single row
        const locationStock = item.locationStock?.[0] || {};
        const locationName = locationStock.locationId?.businessLocationName || locationStock.locationId || "";
        
        const row = {
          "Item Code": item.code || "",
          "Item Name": item.name || "",
          "Description": item.description || "",
          "Ingredients": ingredients,
          "Allergens/Preparation method": allergens,
          "Nutritional info": "",
          "Prep time": item.prepTime || "",
          "Wait time": item.waitTime || "",
          "Currency": item.currency || "",
          "Price": item.price || "",
          "SKU": item.sku || "",
          "GTIN": item.gtin || "",
          "Variant": "Full",
          "Variant desc": "",
          "Variant currency": "",
          "Variant price": "",
          "Asset URLs": assetUrls,
          "Location 1": locationName,
          "Stock on hand 1": locationStock.stockOnHand || 0,
          "Stock committed 1": locationStock.stockCommitted || 0,
          "Awaiting new stock delivery 1": formatDate(locationStock.awaitingNewStockDelivery),
          "Awaiting new stock count 1": locationStock.awaitingNewStockCount || 0,
          "Show stock count to customer 1": item.showStockToCustomer ? "Yes" : "No",
          "Allow sale if out of stock 1": item.allowSaleIfOutOfStock ? "Yes" : "No",
        };
        
        exportRows.push(row);
      }
    });
    
    return exportRows;
  };

  // Export to CSV
  const exportToCSV = () => {
    let items = [];
    if (menuItems) {
      if (menuItems.items && Array.isArray(menuItems.items)) {
        items = menuItems.items;
      } else if (Array.isArray(menuItems)) {
        items = menuItems;
      } else if (menuItems.data && Array.isArray(menuItems.data)) {
        items = menuItems.data;
      }
    }

    let exportData = [];
    let headers = [];
    
    if (items.length === 0) {
      // Export empty template with headers only
      headers = [
        "Item Code", "Item Name", "Description", "Ingredients", "Allergens/Preparation method",
        "Nutritional info", "Prep time", "Wait time", "Currency", "Price", "SKU", "GTIN",
        "Variant", "Variant desc", "Variant currency", "Variant price", "Asset URLs",
        "Location 1", "Stock on hand 1", "Stock committed 1", "Awaiting new stock delivery 1",
        "Awaiting new stock count 1", "Show stock count to customer 1", "Allow sale if out of stock 1"
      ];
      exportData = [];
    } else {
      exportData = transformItemsForExport(items);
      // Get headers
      headers = Object.keys(exportData[0]);
    }
    
    // Create CSV content
    const csvContent = [
      headers.join(","),
      ...exportData.map((row) =>
        headers.map((header) => {
          const value = row[header] || "";
          // Escape commas and quotes in CSV
          if (typeof value === "string" && (value.includes(",") || value.includes('"') || value.includes("\n"))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(",")
      ),
    ].join("\n");
    
    const fileName = items.length === 0 
      ? `menu_items_template_${new Date().toISOString().split("T")[0]}.csv`
      : `menu_items_${new Date().toISOString().split("T")[0]}.csv`;

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(items.length === 0 ? "Empty template exported to CSV" : "Items exported to CSV  ");
  };

  // Export to Excel
  const exportToExcel = () => {
    let items = [];
    if (menuItems) {
      if (menuItems.items && Array.isArray(menuItems.items)) {
        items = menuItems.items;
      } else if (Array.isArray(menuItems)) {
        items = menuItems;
      } else if (menuItems.data && Array.isArray(menuItems.data)) {
        items = menuItems.data;
      }
    }

    let exportData = [];
    let headers = [];
    
    if (items.length === 0) {
      // Export empty template with headers only
      headers = [
        "Item Code", "Item Name", "Description", "Ingredients", "Allergens/Preparation method",
        "Nutritional info", "Prep time", "Wait time", "Currency", "Price", "SKU", "GTIN",
        "Variant", "Variant desc", "Variant currency", "Variant price", "Asset URLs",
        "Location 1", "Stock on hand 1", "Stock committed 1", "Awaiting new stock delivery 1",
        "Awaiting new stock count 1", "Show stock count to customer 1", "Allow sale if out of stock 1"
      ];
      // Create empty row with all headers
      const emptyRow = {};
      headers.forEach(header => {
        emptyRow[header] = "";
      });
      exportData = [emptyRow];
    } else {
      exportData = transformItemsForExport(items);
      headers = Object.keys(exportData[0]);
    }
    
    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);
    
    // Set column widths
    const colWidths = headers.map((key) => ({
      wch: Math.max(key.length, 15),
    }));
    ws["!cols"] = colWidths;
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Menu Items");
    
    // Generate Excel file and download
    const fileName = items.length === 0 
      ? `menu_items_template_${new Date().toISOString().split("T")[0]}.xlsx`
      : `menu_items_${new Date().toISOString().split("T")[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    
    toast.success(items.length === 0 ? "Empty template exported to Excel" : "Items exported to Excel  ");
  };

  // Handle export menu open
  const handleExportMenuOpen = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  // Handle export menu close
  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  // Handle CSV export from menu
  const handleExportCSV = () => {
    handleExportMenuClose();
    exportToCSV();
  };

  // Handle Excel export from menu
  const handleExportExcel = () => {
    handleExportMenuClose();
    exportToExcel();
  };

  // Parse CSV file
  const parseCSV = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target.result;
          const lines = text.split("\n").filter((line) => line.trim());
          if (lines.length < 2) {
            reject(new Error("CSV file must have at least a header row and one data row"));
            return;
          }

          const headers = lines[0].split(",").map((h) => h.trim().replace(/"/g, ""));
          const data = [];

          for (let i = 1; i < lines.length; i++) {
            const values = [];
            let currentValue = "";
            let inQuotes = false;

            for (let j = 0; j < lines[i].length; j++) {
              const char = lines[i][j];
              if (char === '"') {
                if (inQuotes && lines[i][j + 1] === '"') {
                  currentValue += '"';
                  j++;
                } else {
                  inQuotes = !inQuotes;
                }
              } else if (char === "," && !inQuotes) {
                values.push(currentValue.trim());
                currentValue = "";
              } else {
                currentValue += char;
              }
            }
            values.push(currentValue.trim());

            const row = {};
            headers.forEach((header, index) => {
              row[header] = values[index] || "";
            });
            data.push(row);
          }

          resolve(data);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error("Failed to read CSV file"));
      reader.readAsText(file);
    });
  };

  // Parse Excel file
  const parseExcel = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error("Failed to read Excel file"));
      reader.readAsArrayBuffer(file);
    });
  };

  // Helper: Extract base code and variant number from Item Code
  const parseItemCode = (itemCode) => {
    // Convert to string in case it's a number from Excel parsing
    const code = String(itemCode || "").trim();
    if (code.includes("-")) {
      const parts = code.split("-");
      return {
        baseCode: parts[0],
        variantNumber: parts.slice(1).join("-"), // Handle cases like "1002-A-B"
        isVariant: true
      };
    }
    return {
      baseCode: code,
      variantNumber: "",
      isVariant: false
    };
  };

  // Helper: Parse nutritional info from string format
  const parseNutritionalInfo = (nutritionStr) => {
    // Convert to string in case it's a number from Excel parsing
    const nutritionString = String(nutritionStr || "").trim();
    if (!nutritionString) return null;
    
    const nutrition = {};
    const patterns = {
      weight: /Weight:\s*([\d.]+)/i,
      kcal: /Kcal:\s*([\d.]+)/i,
      protein: /Protein:\s*([\d.]+)/i,
      carbs: /Carbs:\s*([\d.]+)/i,
      carbohydrates: /Carbohydrates:\s*([\d.]+)/i,
      fats: /Fats:\s*([\d.]+)/i,
      fibre: /Fibre:\s*([\d.]+)/i,
      fiber: /Fiber:\s*([\d.]+)/i
    };

    Object.entries(patterns).forEach(([key, pattern]) => {
      const match = nutritionString.match(pattern);
      if (match) {
        const value = parseFloat(match[1]);
        if (!isNaN(value)) {
          // Map carbs/carbohydrates to carbohydrates
          if (key === "carbs" || key === "carbohydrates") {
            nutrition.carbohydrates = value;
          } else if (key === "fiber") {
            nutrition.fibre = value;
          } else {
            nutrition[key] = value;
          }
        }
      }
    });

    return Object.keys(nutrition).length > 0 ? nutrition : null;
  };

  // Helper: Convert date from MM/DD/YYYY to YYYY-MM-DD
  const convertDateFormat = (dateStr) => {
    if (!dateStr) return "";
    
    // If it's already a Date object from Excel parsing, convert directly
    if (dateStr instanceof Date) {
      if (!isNaN(dateStr.getTime())) {
        return dateStr.toISOString().split("T")[0];
      }
      return "";
    }
    
    // Handle Excel date serial numbers (days since January 1, 1900)
    // Excel serial numbers are typically between 1 and 100000
    if (typeof dateStr === "number" && dateStr > 0 && dateStr < 1000000) {
      // Excel epoch starts on January 1, 1900
      const excelEpoch = new Date(1899, 11, 30); // December 30, 1899 (Excel's epoch)
      const date = new Date(excelEpoch.getTime() + dateStr * 24 * 60 * 60 * 1000);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split("T")[0];
      }
    }
    
    // Convert to string in case it's a number from Excel parsing
    const dateString = String(dateStr || "").trim();
    if (!dateString) return "";
    
    // Try MM/DD/YYYY format
    const mmddyyyy = dateString.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mmddyyyy) {
      const [, month, day, year] = mmddyyyy;
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }
    
    // If already in YYYY-MM-DD format, return as is
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateString;
    }
    
    // Try parsing as Date object
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split("T")[0];
    }
    
    return ""; // Return empty string if can't parse
  };

  // Helper: Parse location stock from column format (Location 1, Stock on hand 1, etc.)
  const parseLocationStockFromColumns = (row, locationIndex, isVariant = false) => {
    // Convert to string in case it's a number from Excel parsing
    const locationName = String(row[`Location ${locationIndex}`] || "").trim();
    if (!locationName) return null;

    const locationId = locationMap[locationName.toLowerCase()] || locationName;
    
    // For variants, use Variant currency/price; for no variant, use Currency/Price
    const currency = String(isVariant 
      ? (row[`Variant currency`] || "USD")
      : (row[`Currency`] || "USD")).trim();
    const price = isVariant
      ? parseFloat(row[`Variant price`] || 0) || 0
      : parseFloat(row[`Price`] || 0) || 0;
    
    const stockObj = {
      locationId: locationId,
      currency: currency,
      price: price,
      sku: String(row[`SKU`] || "").trim(),
      stockOnHand: parseInt(row[`Stock on hand ${locationIndex}`] || 0) || 0,
      stockCommitted: parseInt(row[`Stock committed ${locationIndex}`] || 0) || 0,
      awaitingNewStockDelivery: convertDateFormat(row[`Awaiting new stock delivery ${locationIndex}`] || ""),
      awaitingNewStockCount: parseInt(row[`Awaiting new stock count ${locationIndex}`] || 0) || 0,
    };

    // Only return if at least location name exists
    return stockObj;
  };

  // Helper: Parse all location stocks (1-10)
  const parseAllLocationStocks = (row, isVariant = false) => {
    const locationStocks = [];
    for (let i = 1; i <= 10; i++) {
      const stock = parseLocationStockFromColumns(row, i, isVariant);
      if (stock && stock.locationId) {
        locationStocks.push(stock);
      }
    }
    return locationStocks;
  };


  // Helper: Group rows by base code and detect variantEnabled
  const groupAndDetectVariants = (rows) => {
    const grouped = {};
    
    // First pass: Group rows by base code
    rows.forEach((row, index) => {
      // Convert to string in case it's a number from Excel parsing
      const itemCode = String(row["Item Code"] || row.Code || "").trim();
      const variant = String(row["Variant"] || row.Variant || "").trim();
      
      if (!itemCode) return;
      
      const { baseCode, variantNumber, isVariant } = parseItemCode(itemCode);
      
      if (!grouped[baseCode]) {
        grouped[baseCode] = {
          baseCode,
          rows: [],
          variantRows: []
        };
      }
      
      const rowData = {
        ...row,
        originalIndex: index,
        itemCode: itemCode,
        variant: variant,
        variantNumber: variantNumber,
        isVariantRow: isVariant,
        hasVariantInfo: variant && variant.toLowerCase() !== "full" && variant !== ""
      };
      
      grouped[baseCode].rows.push(rowData);
      if (rowData.hasVariantInfo) {
        grouped[baseCode].variantRows.push(rowData);
      }
    });
    
    // Second pass: Determine variantEnabled
    Object.keys(grouped).forEach(baseCode => {
      const group = grouped[baseCode];
      // If at least 1 row has variant information (not "Full") → variantEnabled = true
      group.variantEnabled = group.variantRows.length >= 1;
      
      // Set variantEnabled on all rows in group
      group.rows.forEach(row => {
        row.variantEnabled = group.variantEnabled;
      });
    });
    
    return grouped;
  };

  // Transform imported data to match backend payload structure
  const transformImportedData = (rows) => {
    // Step 1: Filter valid rows
    const validRows = rows.filter(row => {
      // Convert to string in case they're numbers from Excel parsing
      const itemCode = String(row["Item Code"] || row.Code || "").trim();
      const itemName = String(row["Item Name"] || row.Name || "").trim();
      return itemCode && itemName;
    });

    if (validRows.length === 0) return [];

    // Step 2: Group rows by base code and detect variants
    const grouped = groupAndDetectVariants(validRows);
    
    // Step 3: Transform each group into backend payload format
    const transformedItems = [];

    Object.keys(grouped).forEach(baseCode => {
      const group = grouped[baseCode];
      // Find base row: prefer non-variant row, otherwise prefer "Full" variant, otherwise first row
      const baseRow = group.rows.find(r => !r.isVariantRow) 
        || group.rows.find(r => r.variant && r.variant.toLowerCase() === "full")
        || group.rows[0];
      
      if (!baseRow) return;

      // Build base item
      const item = {
        code: baseCode,
        name: String(baseRow["Item Name"] || baseRow.Name || "").trim(),
        description: String(baseRow["Description"] || "").trim(),
        variantEnabled: group.variantEnabled,
        isCatalog: isCatalog,
      };

      // Include _id if present
      if (baseRow._id || baseRow["_id"]) {
        item._id = String(baseRow._id || baseRow["_id"] || "").trim();
      }

      // Parse common fields (only for menu items, not catalog)
      if (!isCatalog) {
        if (baseRow["Prep time"]) item.prepTime = parseInt(baseRow["Prep time"]) || undefined;
        if (baseRow["Wait time"]) item.waitTime = parseInt(baseRow["Wait time"]) || undefined;
        
        // Parse ingredients (comma-separated)
        if (baseRow["Ingredients"]) {
          const ingredients = (baseRow["Ingredients"] || "")
            .split(",")
            .map(i => i.trim())
            .filter(Boolean)
            .map(name => ({ name, substitutes: [] }));
          if (ingredients.length > 0) item.ingredients = ingredients;
        }

        // Parse allergens (comma-separated)
        if (baseRow["Allergens/Preparation method"]) {
          const allergens = (baseRow["Allergens/Preparation method"] || "")
            .split(",")
            .map(a => a.trim())
            .filter(Boolean);
          if (allergens.length > 0) item.allergens = allergens;
        }

        // Parse diet types from allergens (if needed)
        // Note: This might need adjustment based on actual data structure
      }

      // Parse asset URLs
      if (baseRow["Asset URLs"]) {
        const imageVideo = (baseRow["Asset URLs"] || "")
          .split(",")
          .map(i => i.trim())
          .filter(Boolean);
        if (imageVideo.length > 0) item.imageVideo = imageVideo;
      }

      // Handle variants or no variant
      if (group.variantEnabled) {
        // Process variants - include ALL variant rows (including "Full" if it's a variant row)
        // This handles cases where all rows have variant numbers (e.g., 1003-01, 1003-02)
        const variants = group.rows
          .filter(r => r.isVariantRow) // Include all rows with variant numbers
          .map(variantRow => {
            const variant = {
              name: variantRow.variant,
              description: String(variantRow["Variant desc"] || variantRow["Description"] || "").trim(),
              code: variantRow.itemCode, // Full code like "1002-01"
            };

            // Parse variant-specific fields
            if (variantRow["SKU"]) {
              variant.sku = String(variantRow["SKU"] || "").trim();
            }
            if (variantRow["GTIN"]) {
              variant.gtin = String(variantRow["GTIN"] || "").trim();
            }
            // Note: currency and price are in perLocationStock, not at variant level

            // Parse nutritional info
            if (variantRow["Nutritional info"]) {
              const nutrition = parseNutritionalInfo(variantRow["Nutritional info"]);
              if (nutrition) {
                variant.nutrition = nutrition;
              }
            }

            // Parse location stock for this variant
            const perLocationStock = parseAllLocationStocks(variantRow, true);
            if (perLocationStock.length > 0) {
              variant.perLocationStock = perLocationStock;
            }

            // For catalog items
            if (isCatalog) {
              if (variantRow["Stock on hand 1"]) {
                variant.stockCount = parseInt(variantRow["Stock on hand 1"]) || 0;
              }
              if (variantRow["Stock committed 1"]) {
                variant.stockCommitted = parseInt(variantRow["Stock committed 1"]) || 0;
              }
              if (variantRow["Awaiting new stock delivery 1"]) {
                variant.awaitingNewDelivery = convertDateFormat(variantRow["Awaiting new stock delivery 1"]);
              }
            } else {
              // For menu items - calculate total stock
              const totalStock = perLocationStock.reduce((sum, loc) => sum + (loc.stockOnHand || 0), 0);
              variant.totalStock = {
                onHand: totalStock,
                committed: perLocationStock.reduce((sum, loc) => sum + (loc.stockCommitted || 0), 0),
                awaitingDeliveryDate: perLocationStock[0]?.awaitingNewStockDelivery || undefined,
                awaitingQty: perLocationStock.reduce((sum, loc) => sum + (loc.awaitingNewStockCount || 0), 0),
              };

              // Stock flags from first location (or base row)
              const firstLocation = perLocationStock[0];
              if (variantRow["Show stock count to customer 1"]) {
                variant.showStockToCustomer = variantRow["Show stock count to customer 1"] === "Yes";
              }
              if (variantRow["Allow sale if out of stock 1"]) {
                variant.allowSaleIfOutOfStock = variantRow["Allow sale if out of stock 1"] === "Yes";
              }
            }

            return variant;
          });

        if (variants.length > 0) {
          item.variants = variants;
        }
      } else {
        // No variant - use base row data
        if (!isCatalog) {
          if (baseRow["Currency"]) item.currency = String(baseRow["Currency"] || "").trim();
          if (baseRow["Price"]) item.price = parseFloat(baseRow["Price"]) || 0;
        }

        // Parse location stock for no variant items
        const locationStock = parseAllLocationStocks(baseRow);
        if (locationStock.length > 0 && !isCatalog) {
          item.locationStock = locationStock;
        }

        // Stock flags
        if (baseRow["Show stock count to customer 1"]) {
          item.showStockToCustomer = baseRow["Show stock count to customer 1"] === "Yes";
        }
        if (baseRow["Allow sale if out of stock 1"]) {
          item.allowSaleIfOutOfStock = baseRow["Allow sale if out of stock 1"] === "Yes";
        }
      }

      transformedItems.push(item);
    });

    return transformedItems;
  };

  // Handle file import
  const handleFileImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const fileExtension = file.name.split(".").pop().toLowerCase();
    
    if (!["csv", "xlsx", "xls"].includes(fileExtension)) {
      toast.error("Please select a CSV or Excel file");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setImportLoading(true);

    try {
      let parsedData;
      if (fileExtension === "csv") {
        parsedData = await parseCSV(file);
      } else {
        parsedData = await parseExcel(file);
      }

      if (!parsedData || parsedData.length === 0) {
        toast.error("No data found in the file");
        setImportLoading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      const transformedData = transformImportedData(parsedData);
      
      if (transformedData.length === 0) {
        toast.error("No valid items found in the file. Please check that Code and Name columns are filled.");
        setImportLoading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      // Call the bulk import API
      bulkImportMenuItemsMutation.mutate(transformedData);
    } catch (error) {
      console.error("Error parsing file:", error);
      toast.error(error.message || "Failed to parse file. Please check the file format.");
      setImportLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Handle import button click
  const handleImportClick = () => {
    if (isDisabled) {
      toast.error("You don't have permission to import items");
      return;
    }
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Check if menus are available for export
  const hasMenusForExport = () => {
    if (!menus) return false;
    if (menus.menus && Array.isArray(menus.menus) && menus.menus.length > 0)
      return true;
    return false;
  };

  // Check if categories are available for export
  const hasCategoriesForExport = () => {
    if (!menuCategory) return false;
    if (menuCategory.categories && Array.isArray(menuCategory.categories) && menuCategory.categories.length > 0) return true;
    return false;
  };

  // Transform menus data for export (simple format matching client expectations)
  const transformMenusForExport = (menus, allLocations = [], allCategories = []) => {
    if (!menus || !Array.isArray(menus)) return [];
    
    // Helper to find location name by ID
    const findLocationNameById = (locationId) => {
      if (!locationId) return "";
      const found = allLocations.find((loc) => loc._id === locationId || String(loc._id) === String(locationId));
      return found?.businessLocationName || found?.name || "";
    };
    
    // Helper to find category name by ID
    const findCategoryNameById = (categoryId) => {
      if (!categoryId) return "";
      const found = allCategories.find((cat) => cat._id === categoryId || String(cat._id) === String(categoryId));
      return found?.name || "";
    };
    
    return menus.map((menu) => {
      const locationIds = menu?.locationIds || [];
      const categoryIds = menu?.categories || [];
      
      // Extract location names
      const locationNames = locationIds
        .map((locId) => {
          if (typeof locId === "object" && locId !== null && locId.businessLocationName) {
            return String(locId.businessLocationName).trim();
          }
          const id = typeof locId === "object" && locId !== null ? locId._id || locId.id : locId;
          return findLocationNameById(id);
        })
        .filter(Boolean);
      
      // Extract category names
      const categoryNames = categoryIds
        .map((catId) => {
          if (typeof catId === "object" && catId !== null && catId.name) {
            return String(catId.name).trim();
          }
          const id = typeof catId === "object" && catId !== null ? catId._id || catId.id : catId;
          return findCategoryNameById(id);
        })
        .filter(Boolean);

      return {
        "Catalog/Menu Name": menu.name || "",
        "Cover Image URL": menu.image || "",
        "Locations": locationNames.join(", "),
        "Categories": categoryNames.join(", "),
      };
    });
  };

  // Export menus to CSV
  const exportMenusToCSV = async () => {
    let exportData = [];
    let headers = [];
    
    if (!menus || !menus.menus || menus.menus.length === 0) {
      // Export empty template with headers only
      headers = ["Catalog/Menu Name", "Cover Image URL", "Locations", "Categories"];
      exportData = [];
    } else {
      // Fetch locations and categories for name lookup
      let allLocations = [];
      let allCategories = [];
      
      try {
        const locationsRes = await axiosInstance.get(
          `${ApiEndPoints.BUSINESS_LOCATION.getAll}/${selectedBusinessId}`
        );
        allLocations = locationsRes.data.data?.locations || [];
      } catch (error) {
        console.warn("Could not fetch locations for export:", error);
      }
      
      try {
        const categoriesRes = await axiosInstance.get(
          `${ApiEndPoints.MENU_CATEGORY.get}/${selectedBusinessId}`,
          {
            params: {
              isCatalog: isCatalog,
            },
          }
        );
        allCategories = categoriesRes.data.data?.categories || [];
      } catch (error) {
        console.warn("Could not fetch categories for export:", error);
      }

      exportData = transformMenusForExport(menus.menus, allLocations, allCategories);
      
      // Get headers
      headers = Object.keys(exportData[0]);
    }
    
    // Create CSV content
    const csvContent = [
      headers.join(","),
      ...exportData.map((row) =>
        headers.map((header) => {
          const value = row[header] || "";
          // Escape commas and quotes in CSV
          if (typeof value === "string" && (value.includes(",") || value.includes('"') || value.includes("\n"))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(",")
      ),
    ].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    const fileName = (!menus || !menus.menus || menus.menus.length === 0)
      ? `menus_template_${new Date().toISOString().split("T")[0]}.csv`
      : `menus_${new Date().toISOString().split("T")[0]}.csv`;
    link.setAttribute("download", fileName);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success((!menus || !menus.menus || menus.menus.length === 0) ? "Empty template exported to CSV" : "Menus exported to CSV  ");
  };

  // Export menus to Excel
  const exportMenusToExcel = async () => {
    let exportData = [];
    let headers = [];
    
    if (!menus || !menus.menus || menus.menus.length === 0) {
      // Export empty template with headers only
      headers = ["Catalog/Menu Name", "Cover Image URL", "Locations", "Categories"];
      // Create empty row with all headers
      const emptyRow = {};
      headers.forEach(header => {
        emptyRow[header] = "";
      });
      exportData = [emptyRow];
    } else {
      // Fetch locations and categories for name lookup
      let allLocations = [];
      let allCategories = [];
      
      try {
        const locationsRes = await axiosInstance.get(
          `${ApiEndPoints.BUSINESS_LOCATION.getAll}/${selectedBusinessId}`
        );
        allLocations = locationsRes.data.data?.locations || [];
      } catch (error) {
        console.warn("Could not fetch locations for export:", error);
      }
      
      try {
        const categoriesRes = await axiosInstance.get(
          `${ApiEndPoints.MENU_CATEGORY.get}/${selectedBusinessId}`,
          {
            params: {
              isCatalog: isCatalog,
            },
          }
        );
        allCategories = categoriesRes.data.data?.categories || [];
      } catch (error) {
        console.warn("Could not fetch categories for export:", error);
      }

      exportData = transformMenusForExport(menus.menus, allLocations, allCategories);
      headers = Object.keys(exportData[0]);
    }
    
    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);
    
    // Set column widths
    const colWidths = headers.map((key) => ({
      wch: Math.max(key.length, 15),
    }));
    ws["!cols"] = colWidths;
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Menus");
    
    // Generate Excel file and download
    const fileName = (!menus || !menus.menus || menus.menus.length === 0)
      ? `menus_template_${new Date().toISOString().split("T")[0]}.xlsx`
      : `menus_${new Date().toISOString().split("T")[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    
    toast.success((!menus || !menus.menus || menus.menus.length === 0) ? "Empty template exported to Excel" : "Menus exported to Excel  ");
  };

  // Transform categories data for export (simple format matching client expectations)
  const transformCategoriesForExport = (categories, allMenuItems = []) => {
    if (!categories || !Array.isArray(categories)) return [];
    
    // Helper to find item name by ID
    const findItemNameById = (itemId) => {
      if (!itemId) return "";
      const found = allMenuItems.find((item) => item._id === itemId || String(item._id) === String(itemId));
      return found?.name || "";
    };
    
    return categories.map((category) => {
      const menuItemIds = category?.menuItemIds || [];
      
      // Extract item names (all items, no limit)
      // Handle both cases: menuItemIds as objects with names, or as IDs that need lookup
      const itemNames = menuItemIds
        .map((item) => {
          // If item is an object with a name property, use it
          if (typeof item === "object" && item !== null && item.name) {
            return String(item.name).trim();
          }
          // If item is an ID (string), look it up in allMenuItems
          const itemId = typeof item === "object" && item !== null ? item._id || item.id : item;
          return findItemNameById(itemId);
        })
        .filter(Boolean);

      // Build export object dynamically with all items
      const exportRow = {
        "Category Name": category.name || "",
        "Cover Image URL": category.image || "",
      };
      
      // Add Item 1, Item 2, Item 3, ... columns dynamically
      itemNames.forEach((name, index) => {
        exportRow[`Item ${index + 1}`] = name;
      });

      return exportRow;
    });
  };

  // Export categories to CSV
  const exportCategoriesToCSV = async () => {
    let exportData = [];
    let headers = [];
    
    if (!menuCategory || !menuCategory.categories || menuCategory.categories.length === 0) {
      // Export empty template with headers only (minimum headers for categories)
      headers = ["Category Name", "Cover Image URL", "Item 1"];
      exportData = [];
    } else {
      // Fetch menu items for name lookup
      let allMenuItems = [];
      try {
        const menuItemsRes = await axiosInstance.get(
          `${ApiEndPoints.MENU_ITEM.get}/${selectedBusinessId}`,
          {
            params: {
              isCatalog: isCatalog,
            },
          }
        );
        const menuItemsData = menuItemsRes.data.data;
        
        // Extract items array from response
        if (menuItemsData?.items && Array.isArray(menuItemsData.items)) {
          allMenuItems = menuItemsData.items;
        } else if (Array.isArray(menuItemsData)) {
          allMenuItems = menuItemsData;
        } else if (menuItemsData?.data && Array.isArray(menuItemsData.data)) {
          allMenuItems = menuItemsData.data;
        }
      } catch (error) {
        console.warn("Could not fetch menu items for export:", error);
        // Continue with export even if menu items fetch fails
      }

      exportData = transformCategoriesForExport(menuCategory.categories, allMenuItems);
      
      // Get headers
      headers = Object.keys(exportData[0]);
    }
    
    // Create CSV content
    const csvContent = [
      headers.join(","),
      ...exportData.map((row) =>
        headers.map((header) => {
          const value = row[header] || "";
          // Escape commas and quotes in CSV
          if (typeof value === "string" && (value.includes(",") || value.includes('"') || value.includes("\n"))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(",")
      ),
    ].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    const fileName = (!menuCategory || !menuCategory.categories || menuCategory.categories.length === 0)
      ? `categories_template_${new Date().toISOString().split("T")[0]}.csv`
      : `categories_${new Date().toISOString().split("T")[0]}.csv`;
    link.setAttribute("download", fileName);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success((!menuCategory || !menuCategory.categories || menuCategory.categories.length === 0) ? "Empty template exported to CSV" : "Categories exported to CSV  ");
  };

  // Export categories to Excel
  const exportCategoriesToExcel = async () => {
    let exportData = [];
    let headers = [];
    
    if (!menuCategory || !menuCategory.categories || menuCategory.categories.length === 0) {
      // Export empty template with headers only (minimum headers for categories)
      headers = ["Category Name", "Cover Image URL", "Item 1"];
      // Create empty row with all headers
      const emptyRow = {};
      headers.forEach(header => {
        emptyRow[header] = "";
      });
      exportData = [emptyRow];
    } else {
      // Fetch menu items for name lookup
      let allMenuItems = [];
      try {
        const menuItemsRes = await axiosInstance.get(
          `${ApiEndPoints.MENU_ITEM.get}/${selectedBusinessId}`,
          {
            params: {
              isCatalog: isCatalog,
            },
          }
        );
        const menuItemsData = menuItemsRes.data.data;
        
        // Extract items array from response
        if (menuItemsData?.items && Array.isArray(menuItemsData.items)) {
          allMenuItems = menuItemsData.items;
        } else if (Array.isArray(menuItemsData)) {
          allMenuItems = menuItemsData;
        } else if (menuItemsData?.data && Array.isArray(menuItemsData.data)) {
          allMenuItems = menuItemsData.data;
        }
      } catch (error) {
        console.warn("Could not fetch menu items for export:", error);
        // Continue with export even if menu items fetch fails
      }

      exportData = transformCategoriesForExport(menuCategory.categories, allMenuItems);
      headers = Object.keys(exportData[0]);
    }
    
    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);
    
    // Set column widths
    const colWidths = headers.map((key) => ({
      wch: Math.max(key.length, 15),
    }));
    ws["!cols"] = colWidths;
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Categories");
    
    // Generate Excel file and download
    const fileName = (!menuCategory || !menuCategory.categories || menuCategory.categories.length === 0)
      ? `categories_template_${new Date().toISOString().split("T")[0]}.xlsx`
      : `categories_${new Date().toISOString().split("T")[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    
    toast.success((!menuCategory || !menuCategory.categories || menuCategory.categories.length === 0) ? "Empty template exported to Excel" : "Categories exported to Excel  ");
  };

  // Handle category export menu open
  const handleCategoryExportMenuOpen = (event) => {
    setCategoryExportMenuAnchor(event.currentTarget);
  };

  // Handle category export menu close
  const handleCategoryExportMenuClose = () => {
    setCategoryExportMenuAnchor(null);
  };

  // Handle CSV export from menu
  const handleCategoryExportCSV = () => {
    handleCategoryExportMenuClose();
    exportCategoriesToCSV();
  };

  // Handle Excel export from menu
  const handleCategoryExportExcel = () => {
    handleCategoryExportMenuClose();
    exportCategoriesToExcel();
  };

  // Transform imported menu data to match IMenu interface
  const transformImportedMenuData = (rows) => {
    return rows
      .map((row) => {
        // Required field - support both "Catalog/Menu Name" and "Name"
        const menuName = row["Catalog/Menu Name"] || row.Name || row["Menu Name"];
        if (!menuName) {
          return null; // Skip rows without required field
        }

        const menu = {
          name: String(menuName).trim(),
        };

        // Optional fields - support both "Cover Image URL" and "Image"
        const imageUrl = row["Cover Image URL"] || row.Image;
        if (imageUrl) {
          menu.image = String(imageUrl).trim();
        }
        
        // Handle isCatalog - use context default (from isCatalog prop)
        if (isCatalog !== undefined) {
          menu.isCatalog = isCatalog;
        } else {
          menu.isCatalog = false; // Default to false per validation schema
        }
        
        // Handle locationIds - parse from "Locations" column (supports old "Locations (csv)" format for backward compatibility)
        const locationsStr = row["Locations"] || row["Locations (csv)"] || row.Locations || "";
        const locationNames = locationsStr
          .split(",")
          .map((name) => String(name).trim())
          .filter(Boolean);
        
        // Pass location names as array (backend will convert names to IDs)
        menu.locationIds = locationNames;
        
        // Handle categories - parse from "Categories" column (supports old "Categories (csv)" format for backward compatibility)
        const categoriesStr = row["Categories"] || row["Categories (csv)"] || row.Categories || "";
        const categoryNames = categoriesStr
          .split(",")
          .map((name) => String(name).trim())
          .filter(Boolean);
        
        // Pass category names as array (backend will convert names to IDs)
        menu.categories = categoryNames;

        return menu;
      })
      .filter(Boolean); // Remove null entries
  };

  // Transform imported category data to match ICategory interface
  const transformImportedCategoryData = (rows) => {
    return rows
      .map((row) => {
        // Required field - support both "Category Name" and "Name"
        const categoryName = row["Category Name"] || row.Name;
        if (!categoryName) {
          return null; // Skip rows without required field
        }

        const category = {
          name: String(categoryName).trim(),
        };

        // Optional fields - support both "Cover Image URL" and "Image"
        const imageUrl = row["Cover Image URL"] || row.Image;
        if (imageUrl) {
          category.image = String(imageUrl).trim();
        }
        
        // Handle isCatalog - use context default (from isCatalog prop)
        if (isCatalog !== undefined) {
          category.isCatalog = isCatalog;
        } else {
          category.isCatalog = false; // Default to false per validation schema
        }
        
        // Handle menuItemIds - dynamically collect item names from all "Item X" columns
        // Pass names as array, backend will handle name-to-ID conversion
        const itemMap = new Map();
        
        // Dynamically find all "Item X" columns (Item 1, Item 2, Item 3, ..., Item N)
        Object.keys(row).forEach((key) => {
          // Match columns like "Item 1", "Item 2", "Item 10", etc.
          const itemMatch = key.match(/^Item\s+(\d+)$/i);
          if (itemMatch) {
            const itemNumber = parseInt(itemMatch[1], 10);
            const itemValue = row[key];
            if (itemValue) {
              const trimmedName = String(itemValue).trim();
              if (trimmedName) {
                itemMap.set(itemNumber, trimmedName);
              }
            }
          }
        });
        
        // Convert map to array, sorted by item number to maintain order
        const itemNames = Array.from(itemMap.entries())
          .sort((a, b) => a[0] - b[0]) // Sort by item number
          .map(([_, name]) => name); // Extract just the names

        // Pass item names as array (backend will convert names to IDs)
        category.menuItemIds = itemNames;

        return category;
      })
      .filter(Boolean); // Remove null entries
  };

  // Handle category file import
  const handleCategoryFileImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const fileExtension = file.name.split(".").pop().toLowerCase();
    
    if (!["csv", "xlsx", "xls"].includes(fileExtension)) {
      toast.error("Please select a CSV or Excel file");
      if (categoryFileInputRef.current) {
        categoryFileInputRef.current.value = "";
      }
      return;
    }

    setCategoryImportLoading(true);

    try {
      let parsedData;
      if (fileExtension === "csv") {
        parsedData = await parseCSV(file);
      } else {
        parsedData = await parseExcel(file);
      }

      if (!parsedData || parsedData.length === 0) {
        toast.error("No data found in the file");
        setCategoryImportLoading(false);
        if (categoryFileInputRef.current) {
          categoryFileInputRef.current.value = "";
        }
        return;
      }

      // Pass item names directly - backend will handle name-to-ID conversion
      const transformedData = transformImportedCategoryData(parsedData);
      
      if (transformedData.length === 0) {
        toast.error("No valid categories found in the file. Please check that Category Name column is filled.");
        setCategoryImportLoading(false);
        if (categoryFileInputRef.current) {
          categoryFileInputRef.current.value = "";
        }
        return;
      }

      // Call the bulk import API
      bulkImportCategoriesMutation.mutate(transformedData);
    } catch (error) {
      console.error("Error parsing file:", error);
      toast.error(error.message || "Failed to parse file. Please check the file format.");
      setCategoryImportLoading(false);
      if (categoryFileInputRef.current) {
        categoryFileInputRef.current.value = "";
      }
    }
  };

  // Handle category import button click
  const handleCategoryImportClick = () => {
    if (isDisabled) {
      toast.error("You don't have permission to import categories");
      return;
    }
    if (categoryFileInputRef.current) {
      categoryFileInputRef.current.click();
    }
  };

  // Handle menu file import
  const handleMenuFileImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const fileExtension = file.name.split(".").pop().toLowerCase();
    
    if (!["csv", "xlsx", "xls"].includes(fileExtension)) {
      toast.error("Please select a CSV or Excel file");
      if (menuFileInputRef.current) {
        menuFileInputRef.current.value = "";
      }
      return;
    }

    setMenuImportLoading(true);

    try {
      let parsedData;
      if (fileExtension === "csv") {
        parsedData = await parseCSV(file);
      } else {
        parsedData = await parseExcel(file);
      }

      if (!parsedData || parsedData.length === 0) {
        toast.error("No data found in the file");
        setMenuImportLoading(false);
        if (menuFileInputRef.current) {
          menuFileInputRef.current.value = "";
        }
        return;
      }

      // Pass location and category names directly - backend will handle name-to-ID conversion
      const transformedData = transformImportedMenuData(parsedData);
      
      if (transformedData.length === 0) {
        toast.error("No valid menus found in the file. Please check that Catalog/Menu Name column is filled.");
        setMenuImportLoading(false);
        if (menuFileInputRef.current) {
          menuFileInputRef.current.value = "";
        }
        return;
      }

      // Call the bulk import API
      bulkImportMenusMutation.mutate(transformedData);
    } catch (error) {
      console.error("Error parsing file:", error);
      toast.error(error.message || "Failed to parse file. Please check the file format.");
      setMenuImportLoading(false);
      if (menuFileInputRef.current) {
        menuFileInputRef.current.value = "";
      }
    }
  };

  // Handle menu import button click
  const handleMenuImportClick = () => {
    if (isDisabled) {
      toast.error("You don't have permission to import menus");
      return;
    }
    if (menuFileInputRef.current) {
      menuFileInputRef.current.click();
    }
  };

  // Render content based on active tab
  const renderTabContent = () => {
    switch (tab) {
      case 0: // Menu tab
        return (
          <>
            {/* Export and Import buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mb: 2,
                mt: 1,
              }}
            >
              {/* Hidden file input */}
              <input
                ref={menuFileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                style={{ display: "none" }}
                onChange={handleMenuFileImport}
              />
              
              {/* Import button */}
              <Button
                variant="outlined"
                size="small"
                startIcon={
                  menuImportLoading || bulkImportMenusMutation.isLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <FileUploadIcon />
                  )
                }
                onClick={handleMenuImportClick}
                disabled={isDisabled || menuImportLoading || bulkImportMenusMutation.isLoading}
                sx={{
                  textTransform: "lowercase",
                  borderColor: "#5a00cc",
                  color: "#f3e7ff",
                  "&:hover": {
                    borderColor: "#5a00cc",
                    color: "#fff",
                    bgcolor: "#5a00cc",
                  },
                  "&:disabled": {
                    borderColor: "#e0e0e0",
                    color: "#bdbdbd",
                  },
                }}
              >
                {menuImportLoading || bulkImportMenusMutation.isLoading ? "importing..." : "import"}
              </Button>

              {/* Export button */}
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportMenuOpen}
                sx={{
                  textTransform: "lowercase",
                  borderColor: "#5a00cc",
                  color: "#f3e7ff",
                  "&:hover": {
                    borderColor: "#5a00cc",
                    color: "#fff",
                    bgcolor: "#5a00cc",
                  },
                  "&:disabled": {
                    borderColor: "#e0e0e0",
                    color: "#bdbdbd",
                  },
                }}
              >
                {hasMenusForExport() ? "export" : "sample"}
              </Button>
              <Menu
                anchorEl={exportMenuAnchor}
                open={Boolean(exportMenuAnchor)}
                onClose={handleExportMenuClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <MenuItem
                  onClick={() => {
                    handleExportMenuClose();
                    exportMenusToCSV();
                  }}
                  sx={{
                    textTransform: "lowercase",
                    fontSize: 14,
                  }}
                >
                  {hasMenusForExport() ? "export to csv" : "sample csv"}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleExportMenuClose();
                    exportMenusToExcel();
                  }}
                  sx={{
                    textTransform: "lowercase",
                    fontSize: 14,
                  }}
                >
                  {hasMenusForExport() ? "export to excel" : "sample excel"}
                </MenuItem>
              </Menu>
            </Box>

            {/* Create menu card */}
            <Box
              sx={{ 
                textAlign: "center", 
                mt: 1, 
                mb: 2,
                cursor: isDisabled ? "not-allowed" : "pointer",
                opacity: isDisabled ? 0.5 : 1,
              }}
              onClick={() => {
                if (isDisabled) return;
                
                const features = selectedBusiness?.features || [];
                //menu
                const menufeature = features.find(
                  (feature) => feature.featureKey === "menu_catalogs"
                );
                if (menufeature.isFree) {
                  createOnMenuClick();
                } else if (menufeature.quantity > menus?.menus?.length) {
                  createOnMenuClick();
                } else {
                  // Show upgrade dialog instead of toast
                  setUpgradeDialogConfig({
                    featureType: "menus",
                    currentQuantity: menus?.menus?.length || 0,
                    maxQuantity: menufeature.quantity,
                  });
                  setShowUpgradeDialog(true);
                }

                // if (tab == 0) {
                //   //menu
                //   const menufeature = features.find(
                //     (feature) => feature.featureKey === "menu_catalogs"
                //   );
                //   if (menufeature.isFree) {
                //     createOnMenuClick();
                //   }
                // } else if (tab == 1) {
                //   //categories

                //   // const categoriesfeature = features.find(
                //   //   (feature) => feature.featureKey === "categories"
                //   // );
                //   // if (categoriesfeature.isFree) {
                //   //   handleAddCategory();
                //   // }
                // } else {
                //   //items
                //   const itemsfeature = features.find(
                //     (feature) => feature.featureKey === "items_per_menu"
                //   );
                //   if (itemsfeature.isFree) {
                //     handleAddCategory();
                //   }
                // }

                // if(menuCatalogsQuantity.isFree){
                //   createOnMenuClick()
                // }else{

                //  console.log("menuCatalogsQuantity", menus);

                // }
                // console.log("itemsPerMenuQuantity", itemsPerMenuQuantity.quantity);
                // console.log("itemsPerMenuQuantity isFree", itemsPerMenuQuantity.isFree);
                // if(itemsPerMenuQuantity.isFree){
                //   setActiveStep(1)
                // }else if(itemsPerMenuQuantity.quantity > 0){
                //   // setActiveStep(1)
                // }else{
                //   // toast.error("You have reached the maximum number of menus");
                // }

                // createOnMenuClick()
              }}
            >
              <Paper
                variant="outlined"
                sx={{
                  border: "2px dashed #BDBDBD",
                  borderRadius: 2,
                  width: 110,
                  height: 110,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: isDisabled ? "#F5F5F5" : "#FAFAFA",
                  mb: 0.5,
                  boxShadow: "none",
                }}
              >
                <AddIcon sx={{ color: isDisabled ? "#D0D0D0" : "#BDBDBD", fontSize: 38 }} />
              </Paper>
              <Typography
                sx={{ 
                  mt: 0.5, 
                  fontSize: 15, 
                  color: isDisabled ? "#999" : "#222", 
                  fontWeight: 500 
                }}
              >
                {isCatalog ? "create catalog" : "create menu"}
              </Typography>
            </Box>

            {/* Menu List */}
            <List sx={{ width: "100%", mt: 0 }}>
              {filteredMenus.length === 0 && (
                <Typography
                  sx={{
                    color: "#bbb",
                    fontSize: 15,
                    mt: 2,
                    textAlign: "center",
                  }}
                >
                  {isCatalog ? "no catalogs found" : "no menus found"}
                </Typography>
              )}
              {filteredMenus.map((menu, idx) => (
                <ListItem
                  key={menu._id}
                  sx={{
                    px: 0,
                    py: 1.2,
                    borderBottom: "1px solid #F0F0F0",
                    alignItems: "center",
                  }}
                  secondaryAction={
                    <ListItemSecondaryAction sx={{ right: 0 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isDisabled) handleEditMenu(menu);
                          }}
                          disabled={isDisabled}
                          sx={{
                            color: isDisabled ? "#ccc" : "#6c00e6",
                            "&:hover": {
                              bgcolor: isDisabled ? "transparent" : "#f3e7ff",
                            },
                            cursor: isDisabled ? "not-allowed" : "pointer",
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isDisabled) handleDeleteMenu(menu);
                          }}
                          disabled={isDisabled || deleteMenuMutation.isLoading}
                          sx={{
                            color: isDisabled ? "#ccc" : "#dc2626",
                            "&:hover": {
                              bgcolor: isDisabled ? "transparent" : "#fef2f2",
                            },
                            cursor: isDisabled ? "not-allowed" : "pointer",
                          }}
                        >
                          {deleteMenuMutation.isLoading &&
                          menuToDelete?._id === menu._id ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Box>
                    </ListItemSecondaryAction>
                  }
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "#222",
                      minWidth: 18,
                      mr: 1.5,
                    }}
                  >
                    {idx + 1}
                  </Typography>
                  <ListItemAvatar>
                    <Avatar
                      variant="rounded"
                      src={menu.image || undefined}
                      alt={menu.name}
                      sx={{
                        width: 54,
                        height: 44,
                        borderRadius: 2.2,
                        bgcolor: "#f5f5f9",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
                        ml: 1,
                      }}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: "#2D3A4A",
                          fontSize: 17,
                          mb: 0.2,
                          textTransform: "lowercase",
                        }}
                      >
                        {menu.name}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        sx={{
                          fontWeight: 400,
                          color: "#7B7B7B",
                          fontSize: 13.2,
                          textTransform: "lowercase",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {menu?.categories && menu?.categories?.length > 0
                          ? (() => {
                              const categoryCount = menu.categories.length;
                              if (categoryCount <= 2) {
                                // Show all categories if 2 or fewer
                                return `categories: ${menu.categories
                                  .map((c) => c?.name)
                                  .join(", ")}`;
                              } else {
                                // Show first 2 categories + count for remaining
                                const firstTwo = menu.categories
                                  .slice(0, 2)
                                  .map((c) => c?.name)
                                  .join(", ");
                                const remainingCount = categoryCount - 2;
                                return `categories: ${firstTwo} +${remainingCount}`;
                              }
                            })()
                          : "categories: —"}
                      </Typography>
                    }
                    sx={{ minWidth: 0, mr: 2 }}
                  />
                </ListItem>
              ))}
            </List>
          </>
        );

      case 1: // Categories tab
        return (
          <>
            {/* Export and Import buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mb: 2,
                mt: 1,
              }}
            >
              {/* Hidden file input */}
              <input
                ref={categoryFileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                style={{ display: "none" }}
                onChange={handleCategoryFileImport}
              />
              
              {/* Import button */}
              <Button
                variant="outlined"
                size="small"
                startIcon={
                  categoryImportLoading || bulkImportCategoriesMutation.isLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <FileUploadIcon />
                  )
                }
                onClick={handleCategoryImportClick}
                disabled={isDisabled || categoryImportLoading || bulkImportCategoriesMutation.isLoading}
                sx={{
                  textTransform: "lowercase",
                  borderColor: "#5a00cc",
                  color: "#f3e7ff",
                  "&:hover": {
                    color: "#fff",
                    bgcolor: "#5a00cc",
                  },
                  "&:disabled": {
                    borderColor: "#e0e0e0",
                    color: "#bdbdbd",
                  },
                }}
              >
                {categoryImportLoading || bulkImportCategoriesMutation.isLoading ? "importing..." : "import"}
              </Button>

              {/* Export button */}
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadIcon />}
                onClick={handleCategoryExportMenuOpen}
                sx={{
                  textTransform: "lowercase",
                  borderColor: "#5a00cc",
                  color: "#f3e7ff",
                  "&:hover": {
                    borderColor: "#5a00cc",
                    color: "#fff",
                    bgcolor: "#5a00cc",
                  },
                  "&:disabled": {
                    borderColor: "#e0e0e0",
                    color: "#bdbdbd",
                  },
                }}
              >
                {hasCategoriesForExport() ? "export" : "sample"}
              </Button>
              <Menu
                anchorEl={categoryExportMenuAnchor}
                open={Boolean(categoryExportMenuAnchor)}
                onClose={handleCategoryExportMenuClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <MenuItem
                  onClick={handleCategoryExportCSV}
                  sx={{
                    textTransform: "lowercase",
                    fontSize: 14,
                  }}
                >
                  {hasCategoriesForExport() ? "export to csv" : "sample csv"}
                </MenuItem>
                <MenuItem
                  onClick={handleCategoryExportExcel}
                  sx={{
                    textTransform: "lowercase",
                    fontSize: 14,
                  }}
                >
                  {hasCategoriesForExport() ? "export to excel" : "sample excel"}
                </MenuItem>
              </Menu>
            </Box>

            {/* Create category card */}
            <Box
              sx={{ 
                textAlign: "center", 
                mt: 1, 
                mb: 2,
                cursor: isDisabled ? "not-allowed" : "pointer",
                opacity: isDisabled ? 0.5 : 1,
              }}
              onClick={() => {
                if (isDisabled) return;
                
                const features = selectedBusiness?.features || [];
                //menu
                const categoriesfeature = features.find(
                  (feature) => feature.featureKey === "categories"
                );
                if (categoriesfeature.isFree) {
                  handleAddCategory();
                } else if (
                  categoriesfeature.quantity > menuCategory?.categories?.length
                ) {
                  handleAddCategory();
                } else {
                  // Show upgrade dialog instead of toast
                  setUpgradeDialogConfig({
                    featureType: "categories",
                    currentQuantity: menuCategory?.categories?.length || 0,
                    maxQuantity: categoriesfeature.quantity,
                  });
                  setShowUpgradeDialog(true);
                }
              }}
            >
              <Paper
                variant="outlined"
                sx={{
                  border: "2px dashed #BDBDBD",
                  borderRadius: 2,
                  width: 110,
                  height: 110,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: isDisabled ? "#F5F5F5" : "#FAFAFA",
                  mb: 0.5,
                  boxShadow: "none",
                }}
              >
                <AddIcon sx={{ color: isDisabled ? "#D0D0D0" : "#BDBDBD", fontSize: 38 }} />
              </Paper>
              <Typography
                sx={{ 
                  mt: 0.5, 
                  fontSize: 15, 
                  color: isDisabled ? "#999" : "#222", 
                  fontWeight: 500 
                }}
              >
                create category
              </Typography>
            </Box>

            {/* Category List */}
            <List sx={{ width: "100%", mt: 0 }}>
              {filteredCategories.length === 0 && (
                <Typography
                  sx={{
                    color: "#bbb",
                    fontSize: 15,
                    mt: 2,
                    textAlign: "center",
                  }}
                >
                  No categories found
                </Typography>
              )}
              {filteredCategories.map((category, idx) => (
                <ListItem
                  key={category._id}
                  sx={{
                    px: 0,
                    py: 1.2,
                    borderBottom: "1px solid #F0F0F0",
                    alignItems: "center",
                  }}
                  secondaryAction={
                    <ListItemSecondaryAction sx={{ right: 0 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isDisabled) handleEditCategory(category);
                          }}
                          disabled={isDisabled}
                          sx={{
                            color: isDisabled ? "#ccc" : "#6c00e6",
                            "&:hover": {
                              bgcolor: isDisabled ? "transparent" : "#f3e7ff",
                            },
                            cursor: isDisabled ? "not-allowed" : "pointer",
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isDisabled) handleDeleteCategory(category);
                          }}
                          disabled={isDisabled || deleteCategoryMutation.isLoading}
                          sx={{
                            color: isDisabled ? "#ccc" : "#dc2626",
                            "&:hover": {
                              bgcolor: isDisabled ? "transparent" : "#fef2f2",
                            },
                            cursor: isDisabled ? "not-allowed" : "pointer",
                          }}
                        >
                          {deleteCategoryMutation.isLoading &&
                          menuToDelete?._id === category._id ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Box>
                    </ListItemSecondaryAction>
                  }
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "#222",
                      minWidth: 18,
                      mr: 1.5,
                    }}
                  >
                    {idx + 1}
                  </Typography>
                  <ListItemAvatar>
                    <Avatar
                      variant="rounded"
                      src={category.image || undefined}
                      alt={category.name}
                      sx={{
                        width: 54,
                        height: 44,
                        borderRadius: 2.2,
                        bgcolor: "#f5f5f9",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
                        ml: 1,
                      }}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: "#2D3A4A",
                          fontSize: 17,
                          mb: 0.2,
                          textTransform: "lowercase",
                        }}
                      >
                        {category.name}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        sx={{
                          fontWeight: 400,
                          color: "#7B7B7B",
                          fontSize: 13.2,
                          textTransform: "lowercase",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {category?.menuItemIds &&
                        category?.menuItemIds?.length > 0
                          ? `items: ${category?.menuItemIds?.length}`
                          : "items: 0"}
                      </Typography>
                    }
                    sx={{ minWidth: 0, mr: 2 }}
                  />
                </ListItem>
              ))}
            </List>
          </>
        );

      case 2: // Items tab
        return (
          <>
            {/* Export and Import buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mb: 2,
                mt: 1,
              }}
            >
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                style={{ display: "none" }}
                onChange={handleFileImport}
              />
              
              {/* Import button */}
              <Button
                variant="outlined"
                size="small"
                startIcon={
                  importLoading || bulkImportMenuItemsMutation.isLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <FileUploadIcon />
                  )
                }
                onClick={handleImportClick}
                disabled={isDisabled || importLoading || bulkImportMenuItemsMutation.isLoading}
                sx={{
                  textTransform: "lowercase",
                  borderColor: "#5a00cc",
                  color: "#f3e7ff",
                  "&:hover": {
                    color: "#fff",
                    bgcolor: "#5a00cc",
                  },
                  "&:disabled": {
                    borderColor: "#e0e0e0",
                    color: "#bdbdbd",
                  },
                }}
              >
                {importLoading || bulkImportMenuItemsMutation.isLoading ? "importing..." : "import"}
              </Button>

              {/* Export button */}
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportMenuOpen}
                sx={{
                  textTransform: "lowercase",
                  borderColor: "#5a00cc",
                  color: "#f3e7ff",
                  "&:hover": {
                    borderColor: "#5a00cc",
                    color: "#fff",
                    bgcolor: "#5a00cc",
                  },
                  "&:disabled": {
                    borderColor: "#e0e0e0",
                    color: "#bdbdbd",
                  },
                }}
              >
                {hasItemsForExport() ? "export" : "sample"}
              </Button>
              <Menu
                anchorEl={exportMenuAnchor}
                open={Boolean(exportMenuAnchor)}
                onClose={handleExportMenuClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <MenuItem
                  onClick={handleExportCSV}
                  sx={{
                    textTransform: "lowercase",
                    fontSize: 14,
                  }}
                >
                  {hasItemsForExport() ? "export to csv" : "sample csv"}
                </MenuItem>
                <MenuItem
                  onClick={handleExportExcel}
                  sx={{
                    textTransform: "lowercase",
                    fontSize: 14,
                  }}
                >
                  {hasItemsForExport() ? "export to excel" : "sample excel"}
                </MenuItem>
              </Menu>
            </Box>

            {/* Create item card */}
            <Box
              sx={{ 
                textAlign: "center", 
                mt: 1, 
                mb: 2,
                cursor: isDisabled ? "not-allowed" : "pointer",
                opacity: isDisabled ? 0.5 : 1,
              }}
              onClick={() => {
                if (isDisabled) return;
                
                const features = selectedBusiness?.features || [];
                const itemsfeature = features.find(
                  (feature) => feature.featureKey === "items_per_menu"
                );
                if (itemsfeature.isFree) {
                  handleAddItem();
                } else if (itemsfeature.quantity > filteredItems?.length) {
                  handleAddItem();
                } else {
                  // Show upgrade dialog instead of toast
                  setUpgradeDialogConfig({
                    featureType: "items",
                    currentQuantity: filteredItems?.length || 0,
                    maxQuantity: itemsfeature.quantity,
                  });
                  setShowUpgradeDialog(true);
                }
              }}
            >
              <Paper
                variant="outlined"
                sx={{
                  border: "2px dashed #BDBDBD",
                  borderRadius: 2,
                  width: 110,
                  height: 110,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: isDisabled ? "#F5F5F5" : "#FAFAFA",
                  mb: 0.5,
                  boxShadow: "none",
                }}
              >
                <AddIcon sx={{ color: isDisabled ? "#D0D0D0" : "#BDBDBD", fontSize: 38 }} />
              </Paper>
              <Typography
                sx={{ 
                  mt: 0.5, 
                  fontSize: 15, 
                  color: isDisabled ? "#999" : "#222", 
                  fontWeight: 500 
                }}
              >
                create item
              </Typography>
            </Box>

            {/* Item List */}
            <List sx={{ width: "100%", mt: 0 }}>
              {filteredItems.length === 0 && (
                <Typography
                  sx={{
                    color: "#bbb",
                    fontSize: 15,
                    mt: 2,
                    textAlign: "center",
                  }}
                >
                  no items found
                </Typography>
              )}
              {filteredItems.map((item, idx) => (
                <ListItem
                  key={item._id}
                  sx={{
                    px: 0,
                    py: 1.2,
                    borderBottom: "1px solid #F0F0F0",
                    alignItems: "center",
                  }}
                  secondaryAction={
                    <ListItemSecondaryAction sx={{ right: 0 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isDisabled) handleEditItem(item);
                          }}
                          disabled={isDisabled}
                          sx={{
                            color: isDisabled ? "#ccc" : "#6c00e6",
                            "&:hover": {
                              bgcolor: isDisabled ? "transparent" : "#f3e7ff",
                            },
                            cursor: isDisabled ? "not-allowed" : "pointer",
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isDisabled) handleDeleteItem(item);
                          }}
                          disabled={isDisabled || deleteMenuItemMutation.isLoading}
                          sx={{
                            color: isDisabled ? "#ccc" : "#dc2626",
                            "&:hover": {
                              bgcolor: isDisabled ? "transparent" : "#fef2f2",
                            },
                            cursor: isDisabled ? "not-allowed" : "pointer",
                          }}
                        >
                          {deleteMenuItemMutation.isLoading &&
                          menuToDelete?._id === item._id ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Box>
                    </ListItemSecondaryAction>
                  }
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "#222",
                      minWidth: 18,
                      mr: 1.5,
                    }}
                  >
                    {idx + 1}
                  </Typography>
                  <ListItemAvatar>
                    <Avatar
                      variant="rounded"
                      src={item.imageVideo?.[0] || undefined}
                      alt={item.name}
                      sx={{
                        width: 54,
                        height: 44,
                        borderRadius: 2.2,
                        bgcolor: "#f5f5f9",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
                        ml: 1,
                      }}
                    >
                      {!item.imageVideo?.[0] && item.name
                        ? item.name.charAt(0).toUpperCase()
                        : "I"}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: "#2D3A4A",
                          fontSize: 17,
                          mb: 0.2,
                          textTransform: "lowercase",
                        }}
                      >
                        {item.name}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography
                          sx={{
                            fontWeight: 400,
                            color: "#7B7B7B",
                            fontSize: 13.2,
                            textTransform: "lowercase",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {item?.price ? `$${item.price} • ` : ""}
                          {item?.description
                            ? item.description
                            : t("menuOptionNoDescription")}
                          {item?.prepTime ? ` • ${t("menuOptionMinPrep", { min: item.prepTime })}` : ""}
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 400,
                            color: "#9CA3AF",
                            fontSize: 11,
                            textTransform: "lowercase",
                            mt: 0.5,
                          }}
                        >
                          {[
                            item?.variants?.length > 0
                              ? t("menuOptionVariantsCount", { count: item.variants.length })
                              : null,
                            item?.allergens?.length > 0
                              ? t("menuOptionAllergensCount", { count: item.allergens.length })
                              : null,
                            item?.ingredients?.length > 0
                              ? `${item.ingredients.length} ingredients`
                              : null,
                          ]
                            .filter(Boolean)
                            .join(" • ")}
                        </Typography>
                      </>
                    }
                    sx={{ minWidth: 0, mr: 2 }}
                  />
                </ListItem>
              ))}
            </List>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Box
        sx={{
          // width: "100vw",
          maxWidth: {
            xs: "100vw",
            sm: "95vw",
            md: 500,
            lg: 600,
            xl: 700,
          },
          mx: "auto",
          bgcolor: "#fff",
          borderRadius: {
            xs: "24px",
            sm: 3,
            md: 4,
          },
          padding: 3,
          overflow: "hidden",
          boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          boxSizing: "border-box",
          "@media (max-width: 600px)": {
            minWidth: "100vw",
            maxWidth: "100vw",
            borderRadius: "24px",
            boxShadow: "0 3px 12px rgba(0,0,0,0.09)",
          },
        }}
      >
        {/* Info icon */}
        <IconButton
          ref={infoIconRef}
          aria-label="Show help"
          onClick={() => setTooltipOpen((open) => !open)}
          sx={{
            position: "absolute",
            top: 18,
            left: 18,
            width: 32,
            height: 32,
            bgcolor: "#232A35",
            color: "#fff",
            "&:hover": { bgcolor: "#232A35" },
          }}
          size="small"
        >
          <InfoOutlinedIcon fontSize="medium" />
        </IconButton>

        {/* Tooltip Popper */}
        <Popper
          open={tooltipOpen}
          anchorEl={infoIconRef.current}
          placement="bottom-start"
          style={{ zIndex: 1300 }}
          modifiers={[
            {
              name: "offset",
              options: {
                offset: [0, 8],
              },
            },
          ]}
        >
          <ClickAwayListener onClickAway={() => setTooltipOpen(false)}>
            <Paper
              elevation={4}
              sx={{
                borderRadius: 2,
                p: 2,
                minWidth: 280,
                maxWidth: 340,
                boxShadow: 4,
                color: "#222",
                fontSize: 15,
              }}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  {isCatalog
                    ? "catalogs, categories, and items:"
                    : "menus, categories, and items:"}
                </Typography>
                <ul style={{ margin: 0, marginBottom: 12, paddingLeft: 22 }}>
                  <li>
                    you can create, edit, or reorder menus, categories, and
                    items anytime under their respective sections.
                  </li>
                  <li>
                    a menu is made up of categories; categories are made up of
                    items
                  </li>
                </ul>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  {isCatalog
                    ? "how to set up your catalog:"
                    : "how to set up your menu:"}
                </Typography>
                <ul style={{ margin: 0, paddingLeft: 22 }}>
                  <li>
                    {isCatalog
                      ? "go to the catalogs section and click create catalog. enter the catalog name and save."
                      : "go to the menus section and click create new menu. enter the menu name and save."}
                  </li>
                  <li>
                    in the categories section, click add category, name it, and
                    assign it to a menu.
                  </li>
                  <li>
                    to add items, go to items and click create new item. enter
                    the item name, price, and description.
                  </li>
                  <li>
                    add variants (e.g., small, medium, large) if applicable, and
                    fill in sku or barcode details.
                  </li>
                  <li>
                    assign the item to the correct category and menu, then click
                    save.
                  </li>
                </ul>
              </Box>
            </Paper>
          </ClickAwayListener>
        </Popper>

        {/* Centered Title */}
        <Typography
          variant="h6"
          align="center"
          sx={{
            color: "#6200EE",
            fontWeight: 700,
            mt: 0,
            mb: 4,
            fontSize: 20,
            letterSpacing: 0.1,
          }}
        >
          {tab === 0 && (isCatalog ? "catalog" : "menu") + " setup"}
          {tab === 1 && "category list"}
          {tab === 2 && "items setup"}
        </Typography>

        {/* Tabs */}
        <Box
          sx={{
            display: "flex",
            bgcolor: "#F4F5F6",
            borderRadius: 12,
            maxWidth: 320,
            mx: "auto",
            mb: 3,
            overflow: "hidden",
          }}
        >
          <Tabs
            value={tab}
            onChange={handleTabChange}
            variant="fullWidth"
            TabIndicatorProps={{ style: { display: "none" } }}
            sx={{
              width: "100%",
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: 16,
                minHeight: 40,
              },
              "& .Mui-selected": {
                bgcolor: "#fff",
                color: "#404040",
                borderRadius: 12,
                boxShadow: "0 1px 4px rgba(0,0,0,0.03)",
              },
              "& .MuiTabs-flexContainer": {
                gap: 0,
              },
            }}
          >
            <Tab label={getTabLabels()[0]} />
            <Tab label={getTabLabels()[1]} />
            <Tab label={getTabLabels()[2]} />
          </Tabs>
        </Box>

        {/* Search box */}
        <Paper
          component="form"
          sx={{
            display: "flex",
            alignItems: "center",
            border: "1px solid #DFE0E1",
            borderRadius: 1,
            px: 1.5,
            mb: 3.5,
            boxShadow: "none",
            bgcolor: "#fff",
          }}
          onSubmit={(e) => e.preventDefault()}
        >
          <SearchIcon sx={{ color: "#BDBDBD", opacity: 0.7, mr: 1 }} />
          <InputBase
            placeholder={
              tab === 0
                ? isCatalog
                  ? t("menuOptionSearchPlaceholderCatalogs")
                  : t("menuOptionSearchPlaceholderMenus")
                : tab === 1
                ? t("menuOptionSearchPlaceholderCategories")
                : t("menuOptionSearchPlaceholderItems")
            }
            sx={{
              flex: 1,
              fontSize: 15,
              height: 36,
              "& input": { py: 0.5 },
            }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <IconButton
            type="button"
            sx={{ p: 0.5, opacity: 0.4 }}
            aria-label="clear"
            onClick={() => setSearch("")}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Paper>

        {/* Render content based on active tab */}
        {renderTabContent()}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            maxWidth: {
              xs: "100vw",
              sm: "95vw",
              md: 500,
              lg: 600,
              xl: 700,
            },
            mx: "auto",
            bgcolor: "#fff",
            borderRadius: {
              xs: "24px",
              sm: 3,
              md: 4,
            },
            overflow: "hidden",
            boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
            "@media (max-width: 600px)": {
              minWidth: "100vw",
              maxWidth: "100vw",
              borderRadius: "24px",
              boxShadow: "0 3px 12px rgba(0,0,0,0.09)",
            },
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {tab === 0 ? t("menuOptionDeleteMenu") : tab === 1 ? t("menuOptionDeleteCategory") : t("menuOptionDeleteItem")}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {tab === 0 ? t("menuOptionAreYouSureDeleteMenu") : tab === 1 ? t("menuOptionAreYouSureDeleteCategory") : t("menuOptionAreYouSureDeleteItem")}
          </Typography>
          <Typography sx={{ color: "#dc2626", fontSize: 14 }}>
            {t("categorySelectCannotUndo")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={() => setShowDeleteDialog(false)}
            sx={{ color: "#fff", textTransform: "lowercase" }}
          >
            {t("categorySelectCancel")}
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            sx={{
              textTransform: "lowercase",
              bgcolor: "#dc2626",
              "&:hover": { bgcolor: "#b91c1c" },
            }}
            disabled={
              deleteMenuMutation.isLoading ||
              deleteCategoryMutation.isLoading ||
              deleteMenuItemMutation.isLoading
            }
          >
            {deleteMenuMutation.isLoading ||
            deleteCategoryMutation.isLoading ||
            deleteMenuItemMutation.isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t("categorySelectDelete")
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upgrade Plan Dialog */}
      <UpgradePlanDialog
        open={showUpgradeDialog}
        onClose={() => setShowUpgradeDialog(false)}
        selectedBusiness={selectedBusiness}
        featureType={upgradeDialogConfig.featureType}
        currentQuantity={upgradeDialogConfig.currentQuantity}
        maxQuantity={upgradeDialogConfig.maxQuantity}
        onUpgrade={() => {
          // Handle upgrade action - you can customize this
          // console.log("Upgrade requested for:", upgradeDialogConfig.featureType);
          dispatch(
            setShowSubscriptionPlanDialog({
              show: true,
              businessId: selectedBusinessId,
              currentPlan: selectedBusiness?.planName,
            })
          );
        }}
      />

      {/* Import Result Dialog */}
      <Dialog
        open={showImportResultDialog}
        onClose={() => setShowImportResultDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxWidth: {
              xs: "100vw",
              sm: "95vw",
              md: 600,
            },
            mx: "auto",
            bgcolor: "#fff",
            borderRadius: {
              xs: "24px",
              sm: 3,
              md: 4,
            },
            overflow: "hidden",
            boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
            "@media (max-width: 600px)": {
              minWidth: "100vw",
              maxWidth: "100vw",
              borderRadius: "24px",
              boxShadow: "0 3px 12px rgba(0,0,0,0.09)",
            },
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {t("menuOptionImportResultsTitle")}
        </DialogTitle>
        <DialogContent>
          {importResult && (
            <Box>
              {/* Summary */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1 }}>
                  {t("menuOptionImportSummary")}
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Typography sx={{ fontSize: 14, color: "#666" }}>
                    {t("menuOptionImportTotalProcessed")}: <strong>{importResult.totalProcessed || importResult.summary?.total || 0}</strong>
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "#10b981", fontWeight: 600 }}>
                    {t("menuOptionImportSucceeded")}: <strong>{importResult.successful?.count || importResult.summary?.succeeded || 0}</strong>
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "#dc2626", fontWeight: 600 }}>
                    {t("menuOptionImportFailed")}: <strong>{importResult.failed?.count || importResult.summary?.failed || 0}</strong>
                  </Typography>
                </Box>
              </Box>

              {/* Successful Items */}
              {importResult.successful?.items && importResult.successful.items.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1, color: "#10b981" }}>
                    {t("menuOptionImportImportedItems")} ({importResult.successful.items.length})
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: 200,
                      overflowY: "auto",
                      border: "1px solid #e5e7eb",
                      borderRadius: 1,
                      p: 1,
                    }}
                  >
                    {importResult.successful.items.map((item, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          p: 1,
                          mb: 0.5,
                          bgcolor: "#f0fdf4",
                          borderRadius: 1,
                        }}
                      >
                        <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                          {item.name || item.code}
                        </Typography>
                        {item.code && (
                          <Typography sx={{ fontSize: 12, color: "#666" }}>
                            {t("menuOptionImportCode")}: {item.code}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Failed Items */}
              {importResult.failed?.items && importResult.failed.items.length > 0 && (
                <Box>
                  <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1, color: "#dc2626" }}>
                    {t("menuOptionImportFailedItems")} ({importResult.failed.items.length})
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: 300,
                      overflowY: "auto",
                      border: "1px solid #fee2e2",
                      borderRadius: 1,
                      p: 1,
                    }}
                  >
                    {importResult.failed.items.map((item, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          p: 1.5,
                          mb: 1,
                          bgcolor: "#fef2f2",
                          borderRadius: 1,
                          borderLeft: "3px solid #dc2626",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "start", gap: 1 }}>
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: "#dc2626",
                              minWidth: 40,
                            }}
                          >
                            #{item.index !== undefined ? item.index + 1 : idx + 1}
                          </Typography>
                          <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
                              {item.name || t("menuOptionUnknown")}
                            </Typography>
                            {item.code && (
                              <Typography sx={{ fontSize: 12, color: "#666", mb: 0.5 }}>
                                code: {item.code}
                              </Typography>
                            )}
                            <Typography
                              sx={{
                                fontSize: 12,
                                color: "#dc2626",
                                fontWeight: 500,
                              }}
                            >
                              {item.error || t("menuOptionUnknownError")}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            onClick={() => setShowImportResultDialog(false)}
            variant="contained"
            sx={{
              textTransform: "lowercase",
              bgcolor: "#5a00cc",
              "&:hover": { bgcolor: "#4a00b3" },
            }}
          >
            close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Import Result Dialog */}
      <Dialog
        open={showCategoryImportResultDialog}
        onClose={() => setShowCategoryImportResultDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxWidth: {
              xs: "100vw",
              sm: "95vw",
              md: 600,
            },
            mx: "auto",
            bgcolor: "#fff",
            borderRadius: {
              xs: "24px",
              sm: 3,
              md: 4,
            },
            overflow: "hidden",
            boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
            "@media (max-width: 600px)": {
              minWidth: "100vw",
              maxWidth: "100vw",
              borderRadius: "24px",
              boxShadow: "0 3px 12px rgba(0,0,0,0.09)",
            },
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {t("menuOptionImportResultsTitle")}
        </DialogTitle>
        <DialogContent>
          {categoryImportResult && (
            <Box>
              {/* Summary */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1 }}>
                  {t("menuOptionImportSummary")}
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Typography sx={{ fontSize: 14, color: "#666" }}>
                    {t("menuOptionImportTotalProcessed")}: <strong>{categoryImportResult.totalProcessed || categoryImportResult.summary?.total || 0}</strong>
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "#10b981", fontWeight: 600 }}>
                    {t("menuOptionImportSucceeded")}: <strong>{categoryImportResult.successful?.count || categoryImportResult.summary?.succeeded || 0}</strong>
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "#dc2626", fontWeight: 600 }}>
                    {t("menuOptionImportFailed")}: <strong>{categoryImportResult.failed?.count || categoryImportResult.summary?.failed || 0}</strong>
                  </Typography>
                </Box>
              </Box>

              {/* Successful Categories */}
              {categoryImportResult.successful?.items && categoryImportResult.successful.items.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1, color: "#10b981" }}>
                    {t("menuOptionImportImportedCategories")} ({categoryImportResult.successful.items.length})
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: 200,
                      overflowY: "auto",
                      border: "1px solid #e5e7eb",
                      borderRadius: 1,
                      p: 1,
                    }}
                  >
                    {categoryImportResult.successful.items.map((category, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          p: 1,
                          mb: 0.5,
                          bgcolor: "#f0fdf4",
                          borderRadius: 1,
                        }}
                      >
                        <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                          {category.name}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Failed Categories */}
              {categoryImportResult.failed?.items && categoryImportResult.failed.items.length > 0 && (
                <Box>
                  <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1, color: "#dc2626" }}>
                    {t("menuOptionImportFailedCategories")} ({categoryImportResult.failed.items.length})
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: 300,
                      overflowY: "auto",
                      border: "1px solid #fee2e2",
                      borderRadius: 1,
                      p: 1,
                    }}
                  >
                    {categoryImportResult.failed.items.map((category, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          p: 1.5,
                          mb: 1,
                          bgcolor: "#fef2f2",
                          borderRadius: 1,
                          borderLeft: "3px solid #dc2626",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "start", gap: 1 }}>
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: "#dc2626",
                              minWidth: 40,
                            }}
                          >
                            #{category.index !== undefined ? category.index + 1 : idx + 1}
                          </Typography>
                          <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
                              {category.name || t("menuOptionUnknown")}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: 12,
                                color: "#dc2626",
                                fontWeight: 500,
                              }}
                            >
                              {category.error || t("menuOptionUnknownError")}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            onClick={() => setShowCategoryImportResultDialog(false)}
            variant="contained"
            sx={{
              textTransform: "lowercase",
              bgcolor: "#5a00cc",
              "&:hover": { bgcolor: "#4a00b3" },
            }}
          >
            close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Menu Import Result Dialog */}
      <Dialog
        open={showMenuImportResultDialog}
        onClose={() => setShowMenuImportResultDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxWidth: {
              xs: "100vw",
              sm: "95vw",
              md: 600,
            },
            mx: "auto",
            bgcolor: "#fff",
            borderRadius: {
              xs: "24px",
              sm: 3,
              md: 4,
            },
            overflow: "hidden",
            boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
            "@media (max-width: 600px)": {
              minWidth: "100vw",
              maxWidth: "100vw",
              borderRadius: "24px",
              boxShadow: "0 3px 12px rgba(0,0,0,0.09)",
            },
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {t("menuOptionImportResultsTitle")}
        </DialogTitle>
        <DialogContent>
          {menuImportResult && (
            <Box>
              {/* Summary */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1 }}>
                  {t("menuOptionImportSummary")}
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Typography sx={{ fontSize: 14, color: "#666" }}>
                    {t("menuOptionImportTotalProcessed")}: <strong>{menuImportResult.totalProcessed || menuImportResult.summary?.total || 0}</strong>
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "#10b981", fontWeight: 600 }}>
                    {t("menuOptionImportSucceeded")}: <strong>{menuImportResult.successful?.count || menuImportResult.summary?.succeeded || 0}</strong>
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "#dc2626", fontWeight: 600 }}>
                    {t("menuOptionImportFailed")}: <strong>{menuImportResult.failed?.count || menuImportResult.summary?.failed || 0}</strong>
                  </Typography>
                </Box>
              </Box>

              {/* Successful Menus */}
              {menuImportResult.successful?.items && menuImportResult.successful.items.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1, color: "#10b981" }}>
                    {t("menuOptionImportImportedMenus")} ({menuImportResult.successful.items.length})
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: 200,
                      overflowY: "auto",
                      border: "1px solid #e5e7eb",
                      borderRadius: 1,
                      p: 1,
                    }}
                  >
                    {menuImportResult.successful.items.map((menu, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          p: 1,
                          mb: 0.5,
                          bgcolor: "#f0fdf4",
                          borderRadius: 1,
                        }}
                      >
                        <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                          {menu.name}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Failed Menus */}
              {menuImportResult.failed?.items && menuImportResult.failed.items.length > 0 && (
                <Box>
                  <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1, color: "#dc2626" }}>
                    {t("menuOptionImportFailedMenus")} ({menuImportResult.failed.items.length})
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: 300,
                      overflowY: "auto",
                      border: "1px solid #fee2e2",
                      borderRadius: 1,
                      p: 1,
                    }}
                  >
                    {menuImportResult.failed.items.map((menu, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          p: 1.5,
                          mb: 1,
                          bgcolor: "#fef2f2",
                          borderRadius: 1,
                          borderLeft: "3px solid #dc2626",
                        }}
                      >
                        <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5, color: "#dc2626" }}>
                          #{menu.index !== undefined ? menu.index + 1 : idx + 1}
                        </Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 500, mb: 0.5 }}>
                          {menu.name || t("menuOptionUnknown")}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "#991b1b" }}>
                          {menu.error || t("menuOptionUnknownError")}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={() => setShowMenuImportResultDialog(false)}
            variant="contained"
            sx={{
              textTransform: "lowercase",
              bgcolor: "#6c00e6",
              "&:hover": { bgcolor: "#5a00cc" },
            }}
          >
            close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
